import { useMutation } from '@apollo/client';
import {
  AnalyticIdentifier,
  isTaxYearTraditional,
  dateStringToReadableMonthYearString,
  questionKeyForPyr,
  dollarsToCents,
  centsToDollars,
} from '@common/neo.tax';
import { InputMasks, ProgressProps, Tooltip, Icon } from '@ui-components/neo.tax';
import { ReactComponent as InfoCircle } from '@ui-components/neo.tax/dist/icons/info-circle.svg';
import { useCallback, useMemo } from 'react';

import { CREDIT_CALCULATION_DEBOUNCE_TIMEOUT_MS, CREDIT_CALCULATION_SERIALIZATION_KEY } from '@neo.tax/rd/constants';
import { useRdContext } from '@neo.tax/rd/context';
import { usePriorYearRevenues, useQuestionSections } from '@neo.tax/rd/hooks';
import {
  Form,
  FormSectionConfiguration,
  FormType,
  getProgress,
  HighlightText,
  makeInputFormConfig,
  validateAmount,
  useAnalyticId,
  useCurrentCompanyId,
  QuestionSectionCode,
  QuestionCode,
  evictCache,
  PriorYearRevenue,
  UPDATE_PRIOR_YEAR_REVENUE,
  UpdatePriorYearRevenueMutationVariables,
  UpdatePriorYearRevenueMutation,
  RevenueSource,
} from '@neo.tax/shared';

import { StyledTooltipContainer } from '../credit-calculation-forms.styled';

const UPDATE_PRIOR_YEAR_REVENUE_KEY = 'UPDATE_PRIOR_YEAR_REVENUE_KEY';
interface PriorYearRevenueForms {
  priorYearRevenueForms: FormSectionConfiguration;
  priorYearRevenueProgress: ProgressProps;
}

export const usePriorYearRevenueForms = (): PriorYearRevenueForms => {
  const priorYearRevenueAnalyticId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_PRIOR_YEAR_REVENUE);
  const companyId = useCurrentCompanyId();
  const { fiscalYear } = useRdContext();
  const { getAnswer, questionSections } = useQuestionSections();
  const { priorYearRevenues: priorYearRevenuesFromGetCompany } = usePriorYearRevenues({ companyId });
  const [updatePriorYearRevenue] = useMutation<UpdatePriorYearRevenueMutation, UpdatePriorYearRevenueMutationVariables>(
    UPDATE_PRIOR_YEAR_REVENUE,
  );

  const firstYearQreAndGrossReceipts = useMemo(() => {
    const firstYearQreAndGrossReceiptsAnswer = getAnswer(
      questionSections,
      QuestionSectionCode.CreditCalculation,
      QuestionCode.FirstYearQreAndPyr,
    );
    return firstYearQreAndGrossReceiptsAnswer ? parseInt(firstYearQreAndGrossReceiptsAnswer) : undefined;
  }, [getAnswer, questionSections]);

  // Derive the displayed revenues purely from the source so changing the first year is reversible.
  // Keeping this as state and filtering it in place lost rows permanently when the first year was
  // raised and then lowered again (only a page refresh restored them).
  const priorYearRevenues = useMemo<PriorYearRevenue[]>(() => {
    if (!priorYearRevenuesFromGetCompany) return [];

    let displayedRevenues = priorYearRevenuesFromGetCompany;

    // Revenues include the claim year, so compare with <= against the fiscal year start date.
    if (fiscalYear) {
      displayedRevenues = displayedRevenues.filter(
        (revenue) => revenue.taxYearStartDate <= fiscalYear.taxYearStartDate,
      );
    }

    if (firstYearQreAndGrossReceipts) {
      displayedRevenues = displayedRevenues.filter((revenue) => revenue.year >= firstYearQreAndGrossReceipts);
    }

    return displayedRevenues;
  }, [priorYearRevenuesFromGetCompany, fiscalYear, firstYearQreAndGrossReceipts]);

  const handlePriorYearRevenueChange = useCallback(
    async (priorYearRevenueId: string, newValue: string) => {
      if (!newValue || newValue === '') {
        return;
      }
      const newAmount = dollarsToCents(parseFloat(newValue));
      const existingRevenue = priorYearRevenuesFromGetCompany?.find((rev) => rev.id === priorYearRevenueId);
      await updatePriorYearRevenue({
        variables: {
          priorYearRevenueId,
          revenueCents: newAmount,
        },
        update: (cache) => {
          evictCache(cache, ['getCreditEstimate']);
        },
        context: {
          // Keyed per revenue so a save to one revenue is not dropped by a save to another within the window.
          debounceKey: `${UPDATE_PRIOR_YEAR_REVENUE_KEY}:${priorYearRevenueId}`,
          debounceTimeout: CREDIT_CALCULATION_DEBOUNCE_TIMEOUT_MS,
          serializationKey: CREDIT_CALCULATION_SERIALIZATION_KEY,
        },
        optimisticResponse: {
          updatePriorYearRevenue: {
            __typename: 'PriorYearRevenue',
            id: priorYearRevenueId,
            revenueCents: newAmount,
            year: existingRevenue?.year ?? 0,
            taxYearStartDate: existingRevenue?.taxYearStartDate ?? '',
            taxYearEndDate: existingRevenue?.taxYearEndDate ?? '',
            revenueSource: existingRevenue?.revenueSource ?? RevenueSource.SelfReported,
          } satisfies PriorYearRevenue,
        },
        onError: (error: Error) => {
          console.error('UpdatePriorYearRevenueMutation in usePriorYearRevenuesForm failed', error);
        },
      });
    },
    [updatePriorYearRevenue, priorYearRevenuesFromGetCompany],
  );

  const priorYearRevenueForms: FormSectionConfiguration = useMemo(
    () => ({
      title: 'Prior Year(s) Revenue',
      subtitle: 'Enter Gross Receipts for the relevant tax years.',
      forms:
        fiscalYear && priorYearRevenues && priorYearRevenues.length > 0
          ? [
              {
                title: 'What were your prior year gross receipts?',
                subtitle: (
                  <>
                    Gross receipts includes all forms of income: revenue from sales, government grants, awards, or
                    interest from bank accounts
                    <StyledTooltipContainer>
                      <Tooltip
                        text="e.g., if you're a C-Corp, this is often the sum of the amounts on line 1c, lines 4 through 8, and line 10 of Form 1120. If you have questions, please reach out to Neo.Tax or your tax preparer."
                        options={{
                          placement: 'top',
                          showArrow: true,
                        }}
                      >
                        <Icon node={<InfoCircle />} size={3} variant="none" colorVariant="info" />
                      </Tooltip>
                    </StyledTooltipContainer>
                  </>
                ),
                formType: FormType.FORM_TITLE,
                key: 'pyr',
                id: priorYearRevenueAnalyticId,
              },
              ...priorYearRevenues.map<Form>((pyr) =>
                makeInputFormConfig({
                  id: pyr.id,
                  title: (
                    <>
                      What were your Gross Receipts in{' '}
                      <HighlightText bold>
                        {isTaxYearTraditional({
                          firstDayOfPeriod: pyr.taxYearStartDate,
                          lastDayOfPeriod: pyr.taxYearEndDate,
                        })
                          ? new Date(pyr.taxYearStartDate).getUTCFullYear()
                          : `Period: ${dateStringToReadableMonthYearString(
                              pyr.taxYearStartDate,
                            )}-${dateStringToReadableMonthYearString(pyr.taxYearEndDate)}`}
                      </HighlightText>
                      ?
                    </>
                  ),
                  key: questionKeyForPyr(pyr.taxYearStartDate),
                  value:
                    pyr.revenueCents === undefined || pyr.revenueCents === null ? '' : centsToDollars(pyr.revenueCents),
                  type: 'text',
                  min: 0,
                  max: 10000000000,
                  mask: InputMasks.NUMBER,
                  maskReturnValueType: 'unmaskedValue',
                  inlineLeadingContent: '$',
                  validator: validateAmount,
                  onChange: (newValue: string): void => {
                    handlePriorYearRevenueChange(pyr.id, newValue);
                  },
                }),
              ),
            ]
          : [],
    }),
    [fiscalYear, priorYearRevenues, priorYearRevenueAnalyticId, handlePriorYearRevenueChange],
  );

  const priorYearRevenueProgress = useMemo(
    () => (priorYearRevenueForms ? getProgress(priorYearRevenueForms.forms) : { total: 0, completed: 0 }),
    [priorYearRevenueForms],
  );

  return {
    priorYearRevenueForms,
    priorYearRevenueProgress,
  };
};
